import { Component } from "@theme/component";
import { onAnimationEnd } from "@theme/utilities";
import { ThemeEvents, CartUpdateEvent } from "@theme/events";

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 * @property {HTMLElement} [cartBubbleA11yCount] - The visually hidden count read by screen readers.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ["cartBubble", "cartBubbleText", "cartBubbleCount"];

  /**
   * The visible count blanks out at 100+, so fall back to the hidden screen
   * reader count, which always carries the full number.
   * @type {number}
   */
  get currentCartCount() {
    // Only trust a plain number: the drawer writes a "99+" sentinel at 100+,
    // which parseInt would read as 99.
    const visibleText = (this.refs.cartBubbleCount.textContent ?? "").trim();
    if (/^\d+$/.test(visibleText)) return Number(visibleText);
    const announced = parseInt(
      this.refs.cartBubbleA11yCount?.textContent ?? "",
      10,
    );
    return Number.isFinite(announced) ? announced : 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : "";
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const cart = /** @type {any} */ (
      event.detail?.resource || event.detail?.data?.cart
    );
    const itemCount = cart?.item_count ?? event.detail.data?.itemCount;
    // Some dispatchers (gift quantity syncs) send no count at all. Rendering
    // that as 0 would hide the bubble and announce an empty cart, so skip.
    if (itemCount === undefined) return;
    const comingFromProductForm =
      event.detail.data?.source === "product-form-component";

    const shouldSetDirectly = cart?.item_count !== undefined;

    this.renderCartBubble(
      itemCount,
      comingFromProductForm && !shouldSetDirectly,
    );
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (
    itemCount,
    comingFromProductForm,
    animate = true,
  ) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle("hidden", itemCount === 0);
    this.refs.cartBubble.classList.toggle("visually-hidden", itemCount === 0);

    // Also remove Tailwind !important variants used by the header bubble
    // when the cart starts empty (cart-bubble.liquid renders !hidden)
    if (itemCount > 0) {
      this.refs.cartBubbleCount.classList.remove("!hidden");
      this.refs.cartBubble.classList.remove("!hidden");
    }
    this.refs.cartBubble.classList.toggle(
      "cart-bubble--animating",
      itemCount > 0 && animate,
    );

    const nextCartCount = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount;

    this.currentCartCount = nextCartCount;

    // Keep the screen reader count in sync with the visible bubble (ACQ-376).
    // The visible count is aria-hidden and blanks out at 100+, so announce the
    // full number. Skip when the running total is unknown (blank visible count).
    if (this.refs.cartBubbleA11yCount && Number.isFinite(nextCartCount)) {
      this.refs.cartBubbleA11yCount.textContent = String(nextCartCount);
    }

    this.classList.toggle("header-actions__cart-icon--has-cart", itemCount > 0);

    sessionStorage.setItem(
      "cart-count",
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      }),
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove("cart-bubble--animating");
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem("cart-count");
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null)
      return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get("cart-icon")) {
  customElements.define("cart-icon", CartIcon);
}
